"use client";
// React Required
import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";

// External Lib
import { Box, IconButton } from "@mui/material";
// Internal Lib
import { AutoComplete, DropdownAutoSuggest, Input, Label, showToast } from "@int-traderjoes/ui-components-library";

// Next Imports
import Image from "next/image";

// Contexts
import { useTheme } from "@/context";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";

import { IManufacturingFacilityInformationFormData, MelissaAddressSuggestion } from "@/context/data.types";
import { DefaultManufacturingFacilityInformationFormData } from '@/context/data';

// constants
import Constants from "@/static/constants/constants.json";

// Utils
import FormHeader from "@/utils/FormHeader";
import { UpArrow, DownArrow } from "@/assets/images/CommonSvgUtils";

// Services
import AuthService from "@/services/authService";

//Assets
import DeleteIcon from "@/assets/icons/Common/DeleteIconTjRed.svg";
import { isValidEmail, validatePhoneNumber, capitalizeText, toNumberIfPossible, formatRawDropdownOptions } from "@/utils/helper";
import ConfirmationModal from "../ConfirmationModal";
import {
  setConfirmationMessage,
  setConfirmationTittle,
  setHighlightedConfirmationMessage,
} from "@/components/DataTable/Datatable.variables";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { handleSuggestionsFetchRequested, setAddressConfirmationMessage } from "@/services/external/mellisa";
import { DropdownData } from "@/types/vendor.types";
import { getDropdownCountryName, getMelissaCountryName } from "@/utils/countryMapping";
import VendorService from "@/services/vendorService";
import VendorGuestService from "@/services/vendorGuestService";
import { handleApiError } from "@/hooks/useApiErrorHandler";
import type { ExistingSite } from "@/types/VendorService.types";
import { hasPersistedManufacturingFacilityId } from "@/utils/vendor/mapManufacturingFacilityForApiPayload";
import {
  getGlobalUnreservedCatalogSiteCount,
} from "@/utils/vendor/siteApiFieldUtils";

const ADDR_RECORDS_COUNT = process.env.NEXT_PUBLIC_MELISSA_ADDRESS_SUGGESTIONS_DEFAULT_COUNT ?? '10';

const NEW_OR_EXISTING_SITE_OPTIONS = [
  { label: "New", value: "new" },
  { label: "Existing", value: "existing" },
];

interface ManufacturingFacilityInformationFormProps {
  manufacturingFacilityInformationFormData?: IManufacturingFacilityInformationFormData;
  maufactureFacilityInfoKey: number;
  onDelete: () => void;
  /** When user confirms delete on a persisted row; parent runs associate API then updates state. */
  onPersistedDeleteConfirm?: () => void | Promise<void>;
  vendorKey?: number;
  onToggleExpand?: (key: number, isExpanded: boolean) => void;
  isVendor?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  isFormDisabled?: boolean;
  dropdownData: DropdownData;
  /** When true (from vendorAssessment.hasExistingVendor), show "Is This New or Existing Site?" dropdown */
  hasExistingVendor?: boolean | null;
  /** Current vendor id - required to fetch existing sites when "Existing" is selected (TJ user flow) */
  vendorId?: string | number | null;
  /** Guest vendor-form: current assessment id for getExistingFacilities when "Existing" is selected */
  guestVendorAssessmentId?: string | null;
  /** Parent-provided catalogue (e.g. AddManufacturingFacility) — single fetch for all rows */
  sharedExistingSitesCatalog?: ExistingSite[];
  sharedExistingSitesLoading?: boolean;
}

const ManufacturingFacilityInformationForm: React.FC<ManufacturingFacilityInformationFormProps> = ({
  manufacturingFacilityInformationFormData,
  maufactureFacilityInfoKey,
  onDelete,
  onPersistedDeleteConfirm,
  vendorKey,
  onToggleExpand,
  isVendor,
  isExpanded,
  onToggle,
  isFormDisabled,
  dropdownData,
  hasExistingVendor,
  vendorId,
  guestVendorAssessmentId,
  sharedExistingSitesCatalog,
  sharedExistingSitesLoading,
}) => {
  const { darkMode } = useTheme();
  const darkModeClass = darkMode ? "dark" : "light";
  const { setIsLoading } = useGlobalStateContext();
  const { languageText } = useLanguageContext();
  const messageTexts = languageText?.MESSAGES;
  const toastMessageTexts = languageText?.MESSAGES?.TOAST_MESSAGES;
  const manufacturingFacilityLabels = languageText?.LABELS?.VENDOR_INFORMATION?.MANUFACTURING_FACILITY_INFO_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.MANUFACTURING_FACILITY_INFO;
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;
  const buttonTexts = languageText?.BUTTONS;
  const deleteSiteConfirmTitle =
    messageTexts?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_TITLE?.VENDOR_SITE_DETAILS_DELETE_SITE_CONFIRM_TITLE;
  const deleteSiteConfirmMessage =
    messageTexts?.CONFIRMATION_BOX_MESSAGES?.CONFIRM_MESSAGE?.VENDOR_SITE_DETAILS_DELETE_SITE_CONFIRM_MSG;
  const displayMode = () => { if (darkMode) { return "dark" } else { return "light" } }
  const { setManufacturingFacilityInformationForms, manufacturingFacilityInformationForms, manufacturingFacilityInformationFormErrors, setManufacturingFacilityInformationFormErrors, isVendorAssessmentSubmitClicked } = useFormContext();
  const payloadRef = useRef<Record<string, string>>({ maxrecords: ADDR_RECORDS_COUNT }); // Persistent object
  const [addressSuggestions, setAddressSuggestions] = useState<MelissaAddressSuggestion[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeletePersistedSiteModalOpen, setIsDeletePersistedSiteModalOpen] = useState(false);
  const [existingSitesList, setExistingSitesList] = useState<ExistingSite[]>([]);
  const [isLoadingExistingSites, setIsLoadingExistingSites] = useState<boolean>(false);
  /** Mirrors selected site id for dropdown display; form state uses `existingSiteId`, mapped to `id` on save */
  const [selectedExistingSiteId, setSelectedExistingSiteId] = useState<string | null>(null);
  /** Guest: avoid repeated getExistingFacilities calls when API returns [] */
  const guestExistingSitesFetchKeyRef = useRef<string>("");
  // Ref to store the previous address object
  const previousAddressRef = useRef({
    street: "",
    city: "",
    state: "",
    // country: "",
    postalCode: "",
  });

  const useSharedExistingSitesCatalog =
    typeof sharedExistingSitesCatalog !== "undefined";
  const effectiveExistingSitesList = useSharedExistingSitesCatalog
    ? sharedExistingSitesCatalog!
    : existingSitesList;
  const effectiveIsLoadingExistingSites = useSharedExistingSitesCatalog
    ? Boolean(sharedExistingSitesLoading)
    : isLoadingExistingSites;

  const currentFormErrors = manufacturingFacilityInformationFormErrors?.[maufactureFacilityInfoKey] || {};
  let manufacturingFormPermissions: any = {};
  // If no data is passed then setting with the defaultvalues form context
  if (!manufacturingFacilityInformationFormData) {
    manufacturingFacilityInformationFormData = DefaultManufacturingFacilityInformationFormData;
  }
  // Fetching permissions for fields
  manufacturingFormPermissions = AuthService.fetchPermissions("manufacturingFacilityInformation");

  const isExistingSite = (manufacturingFacilityInformationFormData as any)?.newOrExistingSite === "existing";

  /** Global catalogue sites left (all rows) — only for New/Existing dropdown lock. */
  const globalUnreservedCatalogSiteCount = useMemo(
    () =>
      getGlobalUnreservedCatalogSiteCount(
        effectiveExistingSitesList,
        manufacturingFacilityInformationForms,
        (f) => ({
          newOrExistingSite: f?.newOrExistingSite,
          existingSiteId: (f as { existingSiteId?: string | number })?.existingSiteId,
          siteName: f?.name,
          rowId: (f as { id?: string | number })?.id,
        })
      ),
    [effectiveExistingSitesList, manufacturingFacilityInformationForms]
  );

  /** Sites not already chosen in another Site Information section (existing pick or new site name; same flow, existing vendor). */
  const filteredExistingSitesList = useMemo(() => {
    const forms = manufacturingFacilityInformationForms;
    if (
      !Array.isArray(forms) ||
      !Array.isArray(effectiveExistingSitesList) ||
      effectiveExistingSitesList.length === 0
    ) {
      return effectiveExistingSitesList;
    }
    const reservedIds = new Set<string>();
    const reservedNames = new Set<string>();
    forms.forEach((f, idx) => {
      if (idx === maufactureFacilityInfoKey) return;
      const siteType = f?.newOrExistingSite;
      if (siteType === "existing") {
        const extId = f?.existingSiteId;
        if (extId != null && String(extId).trim() !== "") {
          reservedIds.add(String(extId));
        }
        const nm = f?.name;
        if (nm != null && String(nm).trim() !== "") {
          reservedNames.add(String(nm).trim());
        }
      } else if (siteType === "new") {
        const nm = f?.name;
        if (nm != null && String(nm).trim() !== "") {
          reservedNames.add(String(nm).trim());
        }
      }
    });
    return effectiveExistingSitesList
      .filter((s) => {
      const sid = String(s.id);
      if (reservedIds.has(sid)) return false;
      const sname = (s.name ?? "").trim();
      if (sname !== "" && reservedNames.has(sname)) return false;
      return true;
    });
  }, [manufacturingFacilityInformationForms, effectiveExistingSitesList, maufactureFacilityInfoKey]);

  /** Dropdown options for this row: filtered list plus current row's saved catalogue site if it was excluded by filtering */
  const existingSiteDropdownOptionsForRow = useMemo(() => {
    const opts = filteredExistingSitesList.map((s) => ({
      label: s.name ?? "",
      value: s.id,
    }));
    const formData = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey];
    const rawId = (formData as { existingSiteId?: string | number })?.existingSiteId;
    const idStr = rawId != null && String(rawId).trim() !== "" ? String(rawId) : "";
    if (idStr === "") return opts;
    const already = opts.some((o) => String(o.value) === idStr);
    if (already) return opts;
    const fromFull = effectiveExistingSitesList.find((s) => String(s.id) === idStr);
    if (fromFull) {
      return [{ label: fromFull.name ?? "", value: fromFull.id }, ...opts];
    }
    return opts;
  }, [
    filteredExistingSitesList,
    effectiveExistingSitesList,
    manufacturingFacilityInformationForms,
    maufactureFacilityInfoKey,
  ]);

  const manufacturingFacilitySelectedExistingOption = useMemo(() => {
    const opts = existingSiteDropdownOptionsForRow;
    const currentName = manufacturingFacilityInformationFormData?.name;
    const extId = (manufacturingFacilityInformationFormData as { existingSiteId?: string | number })
      ?.existingSiteId;
    const hasCurrentName = currentName != null && String(currentName).trim() !== "";
    const hasExtId = extId != null && String(extId).trim() !== "";
    // Nothing selected (e.g. after the clear X): no value, so the placeholder shows
    if (!hasCurrentName && !hasExtId && selectedExistingSiteId == null) return null;
    if (!effectiveExistingSitesList.length) {
      if (currentName != null && String(currentName).trim() !== "") {
        const idStr =
          extId != null && String(extId).trim() !== "" ? String(extId) : String(currentName);
        return { label: String(currentName), value: idStr };
      }
      return null;
    }
    const site =
      (extId != null && String(extId).trim() !== ""
        ? effectiveExistingSitesList.find((s) => String(s.id) === String(extId))
        : undefined) ||
      (currentName != null || selectedExistingSiteId != null
        ? effectiveExistingSitesList.find(
            (s) => s.name === currentName || String(s.id) === String(selectedExistingSiteId)
          )
        : undefined);
    if (!site) {
      return currentName ? { label: String(currentName), value: String(currentName) } : null;
    }
    return opts.find((opt) => String(opt.value) === String(site.id)) ?? null;
  }, [
    existingSiteDropdownOptionsForRow,
    effectiveExistingSitesList,
    manufacturingFacilityInformationFormData?.name,
    manufacturingFacilityInformationFormData,
    selectedExistingSiteId,
  ]);

  const canLoadExistingSitesCatalog = useMemo(() => {
    if (isVendor === true) {
      return (
        guestVendorAssessmentId != null &&
        String(guestVendorAssessmentId).trim() !== ""
      );
    }
    return vendorId != null && String(vendorId).trim() !== "";
  }, [isVendor, guestVendorAssessmentId, vendorId]);

  /**
   * Disable New/Existing when no remaining existing sites are available for this row.
   */
  const shouldLockNewOrExistingSiteToNewOnly = useMemo(() => {
    if (!hasExistingVendor || !canLoadExistingSitesCatalog) return false;
    if (effectiveIsLoadingExistingSites) return false;
    if (
      String(
        (manufacturingFacilityInformationFormData as { newOrExistingSite?: string })
          ?.newOrExistingSite ?? ""
      ).toLowerCase() === "existing"
    ) {
      return false;
    }
    return globalUnreservedCatalogSiteCount === 0;
  }, [
    hasExistingVendor,
    canLoadExistingSitesCatalog,
    effectiveIsLoadingExistingSites,
    manufacturingFacilityInformationFormData,
    globalUnreservedCatalogSiteCount,
  ]);

  const newOrExistingSiteDropdownDisabled =
    Boolean(isFormDisabled) || shouldLockNewOrExistingSiteToNewOnly;

  useLayoutEffect(() => {
    if (!hasExistingVendor || !canLoadExistingSitesCatalog) return;
    if (effectiveIsLoadingExistingSites) return;
    if (filteredExistingSitesList.length > 0) return;
    const mode = String(
      manufacturingFacilityInformationFormData?.newOrExistingSite ?? ""
    ).toLowerCase();
    if (mode !== "existing") return;

    setSelectedExistingSiteId(null);
    setManufacturingFacilityInformationForms((prevForms) => {
      const updatedForms = [...prevForms];
      const existingForm =
        updatedForms[maufactureFacilityInfoKey] ??
        ({ ...DefaultManufacturingFacilityInformationFormData } as IManufacturingFacilityInformationFormData);
      const keepId = (existingForm as { id?: unknown }).id;
      updatedForms[maufactureFacilityInfoKey] = {
        ...DefaultManufacturingFacilityInformationFormData,
        ...(keepId != null && String(keepId).trim() !== ""
          ? { id: keepId as string | number }
          : {}),
        newOrExistingSite: "new",
      };
      return updatedForms;
    });
  }, [
    hasExistingVendor,
    canLoadExistingSitesCatalog,
    effectiveIsLoadingExistingSites,
    filteredExistingSitesList.length,
    manufacturingFacilityInformationFormData?.newOrExistingSite,
    maufactureFacilityInfoKey,
    setManufacturingFacilityInformationForms,
  ]);

  // Fetch existing sites when "Existing" is selected (TJ: from handler; guest: from effect when assessment id is ready)
  const fetchExistingSites = useCallback(() => {
    if (useSharedExistingSitesCatalog) return;
    if (!hasExistingVendor) {
      setExistingSitesList([]);
      return;
    }
    const isGuestFlow = isVendor === true;
    if (isGuestFlow) {
      const aid = guestVendorAssessmentId?.trim() ?? "";
      if (aid === "") {
        setExistingSitesList([]);
        return;
      }
      setIsLoadingExistingSites(true);
      VendorGuestService.getGuestExistingFacilities(aid)
        .then((sites) => {
          if (Array.isArray(sites)) setExistingSitesList(sites);
          else setExistingSitesList([]);
        })
        .catch((err) => {
          handleApiError(
            err,
            toastMessageTexts?.FETCH_VENDOR_INFORMATION ??
              "Failed to fetch existing sites"
          );
          setExistingSitesList([]);
        })
        .finally(() => setIsLoadingExistingSites(false));
      return;
    }
    if (vendorId == null || String(vendorId).trim() === "") {
      setExistingSitesList([]);
      return;
    }
    setIsLoadingExistingSites(true);
    VendorService.getExistingSites(vendorId)
      .then((sites) => {
        if (Array.isArray(sites)) setExistingSitesList(sites);
        else setExistingSitesList([]);
      })
      .catch((err) => {
        handleApiError(err, toastMessageTexts?.FETCH_VENDOR_INFORMATION ?? "Failed to fetch existing sites");
        setExistingSitesList([]);
      })
      .finally(() => setIsLoadingExistingSites(false));
  }, [
    useSharedExistingSitesCatalog,
    hasExistingVendor,
    isVendor,
    guestVendorAssessmentId,
    vendorId,
    toastMessageTexts?.FETCH_VENDOR_INFORMATION,
  ]);

  // Guest vendor-form: prefetch catalogue when hasExistingVendor + assessment id (VendorGuestService)
  useEffect(() => {
    if (useSharedExistingSitesCatalog) return;
    if (isVendor !== true || !hasExistingVendor) {
      guestExistingSitesFetchKeyRef.current = "";
      return;
    }
    const aid = guestVendorAssessmentId?.trim() ?? "";
    if (!aid || isLoadingExistingSites) return;
    const key = `${maufactureFacilityInfoKey}:${aid}`;
    if (guestExistingSitesFetchKeyRef.current === key) return;
    guestExistingSitesFetchKeyRef.current = key;
    fetchExistingSites();
  }, [
    useSharedExistingSitesCatalog,
    isVendor,
    hasExistingVendor,
    guestVendorAssessmentId,
    maufactureFacilityInfoKey,
    isLoadingExistingSites,
    fetchExistingSites,
  ]);

  // Vendor champion / vendor assessment (authenticated): prefetch when hasExistingVendor + vendorId
  useEffect(() => {
    if (useSharedExistingSitesCatalog) return;
    if (isVendor === true || !hasExistingVendor) return;
    if (vendorId == null || String(vendorId).trim() === "") return;
    fetchExistingSites();
  }, [isVendor, hasExistingVendor, vendorId, fetchExistingSites, useSharedExistingSitesCatalog]);

  // Sync selectedExistingSiteId and existingSiteId when form loads with existing site (match by id or name once sites list is available)
  useEffect(() => {
    const formData = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey];
    if (formData?.newOrExistingSite !== "existing" || !effectiveExistingSitesList?.length) {
      if (formData?.newOrExistingSite !== "existing") {
        setSelectedExistingSiteId(null);
      }
      return;
    }
    const extId = (formData as { existingSiteId?: string | number }).existingSiteId;
    const matchById =
      extId != null && String(extId).trim() !== ""
        ? effectiveExistingSitesList.find((s) => String(s.id) === String(extId))
        : undefined;
    const match =
      matchById ||
      (formData?.name != null && String(formData.name).trim() !== ""
        ? effectiveExistingSitesList.find((s) => s.name === formData.name)
        : undefined);
    if (match) {
      const idStr = String(match.id);
      setSelectedExistingSiteId(idStr);
      const currentId = formData.existingSiteId;
      if (currentId == null || String(currentId).trim() === "") {
        setManufacturingFacilityInformationForms((prev) => {
          if (!Array.isArray(prev)) return prev;
          const updated = [...prev];
          const row = updated[maufactureFacilityInfoKey];
          if (row == null) return prev;
          updated[maufactureFacilityInfoKey] = { ...row, existingSiteId: idStr };
          return updated;
        });
      }
    } else {
      setSelectedExistingSiteId(null);
    }
  }, [
    maufactureFacilityInfoKey,
    manufacturingFacilityInformationForms,
    effectiveExistingSitesList,
    setManufacturingFacilityInformationForms,
  ]);

  const updatePayload = (field: keyof IManufacturingFacilityInformationFormData, value: string) => {
    // Map fields to corresponding payload keys
    const fieldMap: { [key in keyof IManufacturingFacilityInformationFormData]: string } = {
      manufacturerAddress: "address1",
      manufacturerCity: "locality",
      manufacturerState: "administrativearea",
      manufacturerCountry: "country",
      manufacturerZip: "postalcode",
      name: "",
      contact: "",
      emailId: "",
      phone: ""
    };

    const payloadKey = fieldMap[field];

    if (payloadKey) {
      payloadRef.current = { ...payloadRef.current, [payloadKey]: value }; // Merge old and new values
    }
  };

  const handleSelectAlternative = () => {
    // handleSuggestionsFetchRequested()
    setTimeout(() => {
      const inputElement = document.getElementById("manufacturer-address-input");
      inputElement?.focus();
    }, 300); // Delay to allow modal to close and input to mount
    setIsModalOpen(false);

  }

  // Handler function for AutoComplete input change
  const handleAutoCompleteInputChange =
    (field: keyof IManufacturingFacilityInformationFormData) =>
      (newValue: string) => {
        updatePayload(field, newValue);
        // Also update the payload with the current country value for better suggestions
        
        if (manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey]?.manufacturerCountry && dropdownData.countries.length > 0) {
          const countryId = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey]?.manufacturerCountry;
          const countryName = dropdownData.countries.find((c: any) => 
            (isNaN(Number(c.id)) ? c.id : Number(c.id)) === (isNaN(Number(countryId)) ? countryId : Number(countryId))
          )?.name;
          
          if (countryName) {
            // Map country name for Melissa API compatibility
            const melissaCountryName = getMelissaCountryName(countryName);
            updatePayload("manufacturerCountry", melissaCountryName);
          }
        }
        
        setManufacturingFacilityInformationForms((prevForms) => {
          console.log("prevForms: ", prevForms);

          const updatedForms = [...prevForms];
          const existingForm: IManufacturingFacilityInformationFormData =
            updatedForms?.[maufactureFacilityInfoKey] ?? {
              name: "",
              contact: "",
              emailId: "",
              phone: "",
              manufacturerZip: "",
              manufacturerAddress: "",
              manufacturerCity: "",
              manufacturerState: "",
              manufacturerCountry: "",
            };
          updatedForms[maufactureFacilityInfoKey] = {
            ...existingForm,
            [field]: newValue,
          };
          return updatedForms;
        });
      };

  // Handler function for when a suggestion is selected
  const onSuggestionSelected = (
    event: React.FormEvent,
    { suggestion }: { suggestion: MelissaAddressSuggestion }
  ) => {
    const selectedAddress = suggestion.Address;

     // Find the country ID that matches the country from Melissa API
    // Try to match by ISO3166_N (numeric country code) first, then by name
    let countryMatch = dropdownData?.countries.find(
      country => country.id === selectedAddress?.ISO3166_N
    );
    
    // If no match by ISO code, try matching by country name
    if (!countryMatch) {
      countryMatch = dropdownData?.countries.find(
        country => country.name === selectedAddress?.CountryName
      );
    }
    
    // If still no match, try reverse mapping (Melissa "United States" -> dropdown "United States Of America")
    if (!countryMatch) {
      const mappedCountryName = getDropdownCountryName(selectedAddress?.CountryName);
      countryMatch = dropdownData?.countries.find(
        country => country.name === mappedCountryName
      );
    }

    setManufacturingFacilityInformationForms((prevForms) => {
      const updatedForms = [...prevForms];
      const existingForm: IManufacturingFacilityInformationFormData =
        updatedForms?.[maufactureFacilityInfoKey] ?? {
          name: "",
          contact: "",
          emailId: "",
          phone: "",
          manufacturerZip: "",
          manufacturerAddress: "",
          manufacturerCity: "",
          manufacturerState: "",
          manufacturerCountry: "",
        };
      updatedForms[maufactureFacilityInfoKey] = {
        ...existingForm,
        manufacturerZip: selectedAddress.PostalCode,
        manufacturerAddress: selectedAddress.Address1,
        // manufacturerCountry: selectedAddress.CountryName,
        manufacturerCity: selectedAddress.Locality,
        manufacturerState: selectedAddress.AdministrativeArea,
      };
      return updatedForms;
    });
    // Perform validation on the selected address and update the state considering the latest state
    setManufacturingFacilityInformationFormErrors(prevErrors => {
      const updatedErrors = Array.isArray(prevErrors) ? [...prevErrors] : [];
      const errorsForCurrentForm = { ...(updatedErrors[maufactureFacilityInfoKey] || {}) };
      // Address validation: Address1 should not be empty
      if (!selectedAddress.Address1 || selectedAddress.Address1.trim().length === 0) {
        errorsForCurrentForm.manufacturerAddress = true;
      } else {
        delete errorsForCurrentForm.manufacturerAddress;
      }
      // City validation
      if (!selectedAddress.Locality || selectedAddress.Locality.trim().length === 0) {
        errorsForCurrentForm.manufacturerCity = true;
      } else {
        delete errorsForCurrentForm.manufacturerCity;
      }
      // State validation
      if (!selectedAddress.AdministrativeArea || selectedAddress.AdministrativeArea.trim().length === 0) {
        errorsForCurrentForm.manufacturerState = true;
      } else {
        delete errorsForCurrentForm.manufacturerState;
      }
      // Country validation: must resolve to a valid dropdown country
      if (!countryMatch) {
        errorsForCurrentForm.manufacturerCountry = true;
      } else {
        delete errorsForCurrentForm.manufacturerCountry;
      }
      // ZIP validation: must be present and meet minimum length
      if (!selectedAddress.PostalCode || selectedAddress.PostalCode.trim().length < Constants.ZIP_CODE_MIN_LENGTH) {
        errorsForCurrentForm.manufacturerZip = true;
      } else {
        delete errorsForCurrentForm.manufacturerZip;
      }
      updatedErrors[maufactureFacilityInfoKey] = errorsForCurrentForm;
      return updatedErrors;
    });

    // Also update the form's country if a valid match, else leave as previous value
    setManufacturingFacilityInformationForms((prevForms) => {
      const updatedForms = [...prevForms];
      const existingForm: IManufacturingFacilityInformationFormData =
        updatedForms?.[maufactureFacilityInfoKey] ?? {
          name: "",
          contact: "",
          emailId: "",
          phone: "",
          manufacturerZip: "",
          manufacturerAddress: "",
          manufacturerCity: "",
          manufacturerState: "",
          manufacturerCountry: "",
        };
      updatedForms[maufactureFacilityInfoKey] = {
        ...existingForm,
        manufacturerZip: selectedAddress.PostalCode,
        manufacturerAddress: selectedAddress.Address1,
        manufacturerCity: selectedAddress.Locality,
        manufacturerState: selectedAddress.AdministrativeArea,
        manufacturerCountry: countryMatch ? countryMatch.id : existingForm.manufacturerCountry,
      };
      return updatedForms;
    });
  };

  const onSuggestionsClearRequested = (): void => {
    setAddressSuggestions([]);
  };
  // Input Change Handler
  const handleInputChange =
    (field: keyof IManufacturingFacilityInformationFormData) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetValue = e.target.value;
        // Validate Manufacturer's Phone
        if (field === "phone" && !validatePhoneNumber(targetValue)) {
          return; // Stop further updates if invalid
        }
        setManufacturingFacilityInformationForms((prevForms) => {
          const updatedForms = [...prevForms];
          const existingForm: IManufacturingFacilityInformationFormData =
            updatedForms?.[maufactureFacilityInfoKey] ?? {
              name: "",
              contact: "",
              emailId: "",
              phone: "",
              manufacturerZip: "",
              manufacturerAddress: "",
              manufacturerCity: "",
              manufacturerState: "",
              manufacturerCountry: "",
            };
          updatedForms[maufactureFacilityInfoKey] = {
            ...existingForm,
            [field]: targetValue,
          };
          return updatedForms;
        });
      };

  // Handler for browser autofill (onInput event fires for autofill)
  const handleInput =
    (field: keyof IManufacturingFacilityInformationFormData) =>
      (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.currentTarget;
        const newValue = target.value;
        const currentForm = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey];
        
        // Only process if value actually changed (autofill detection)
        if (newValue !== currentForm?.[field]) {
          // Validate Manufacturer's Phone
          if (field === "phone" && !validatePhoneNumber(newValue)) {
            return; // Stop further updates if invalid
          }
          
          setManufacturingFacilityInformationForms((prevForms) => {
            const updatedForms = [...prevForms];
            const existingForm: IManufacturingFacilityInformationFormData =
              updatedForms?.[maufactureFacilityInfoKey] ?? {
                name: "",
                contact: "",
                emailId: "",
                phone: "",
                manufacturerZip: "",
                manufacturerAddress: "",
                manufacturerCity: "",
                manufacturerState: "",
                manufacturerCountry: "",
              };
            updatedForms[maufactureFacilityInfoKey] = {
              ...existingForm,
              [field]: newValue,
            };
            return updatedForms;
          });

          // Clear error if field now has a value
          if (newValue) {
            const updatedErrors = Array.isArray(manufacturingFacilityInformationFormErrors) 
              ? [...manufacturingFacilityInformationFormErrors] 
              : [];
            updatedErrors[maufactureFacilityInfoKey] = {
              ...updatedErrors[maufactureFacilityInfoKey],
              [field]: false,
            };
            setManufacturingFacilityInformationFormErrors(updatedErrors);
          }
        }
      };
      
  // When user selects an existing site from dropdown, populate form and set existingSiteId (mapped to `id` on save)
  const handleExistingSiteSelect = (option: any) => {
    const isCleared = option == null || option.value == null || option.value === "";
    if (isCleared) {
      // Unselect: stay in "Existing" mode but clear the chosen site and the fields it populated
      setSelectedExistingSiteId(null);
      setAddressSuggestions([]);
      setManufacturingFacilityInformationForms((prevForms) => {
        const updatedForms = [...prevForms];
        const existingForm: any =
          updatedForms?.[maufactureFacilityInfoKey] ?? { ...DefaultManufacturingFacilityInformationFormData };
        const next: any = {
          ...existingForm,
          name: "",
          contact: "",
          emailId: "",
          phone: "",
          manufacturerAddress: "",
          manufacturerCity: "",
          manufacturerState: "",
          manufacturerZip: "",
          manufacturerCountry: "",
          newOrExistingSite: "existing",
        };
        delete next.existingSiteId;
        updatedForms[maufactureFacilityInfoKey] = next;
        return updatedForms;
      });
      return;
    }
    const siteId = String(option.value);
    const site = effectiveExistingSitesList.find((s) => String(s.id) === siteId);
    if (!site) return;
    setSelectedExistingSiteId(String(site.id));
    setManufacturingFacilityInformationForms((prevForms) => {
      const updatedForms = [...prevForms];
      const existingForm = updatedForms?.[maufactureFacilityInfoKey] ?? DefaultManufacturingFacilityInformationFormData;
      updatedForms[maufactureFacilityInfoKey] = {
        ...existingForm,
        existingSiteId: siteId,
        newOrExistingSite: "existing",
        name: site.name ?? existingForm.name,
        contact: site.contact ?? existingForm.contact,
        emailId: site.emailId ?? existingForm.emailId,
        phone: site.phone ?? existingForm.phone,
        manufacturerAddress: site.address ?? existingForm.manufacturerAddress,
        manufacturerCity: site.city ?? existingForm.manufacturerCity,
        manufacturerState: site.state ?? existingForm.manufacturerState,
        manufacturerZip: site.zip ?? existingForm.manufacturerZip,
        manufacturerCountry: site.country ?? existingForm.manufacturerCountry,
      };
      return updatedForms;
    });
  };

  // Dropdown Change Handler
  const handleDropdownChange =
  (field: keyof IManufacturingFacilityInformationFormData) =>
    (_: React.ChangeEvent<HTMLInputElement>, option: any) => {
      const isCleared =
        option == null || option.value === "" || option.value == null;

      if (isCleared && field === "newOrExistingSite") {
        // Clear (X): nothing selected ("") - reset every site field and drop the selected existing site
        if (!hasExistingVendor && !useSharedExistingSitesCatalog) {
          setExistingSitesList([]);
        }
        setSelectedExistingSiteId(null);
        setAddressSuggestions([]);
        setManufacturingFacilityInformationForms(prevForms => {
          const updatedForms = [...prevForms];
          const existingForm: IManufacturingFacilityInformationFormData =
            updatedForms?.[maufactureFacilityInfoKey] ?? { ...DefaultManufacturingFacilityInformationFormData };
          const next: any = {
            ...existingForm,
            ...DefaultManufacturingFacilityInformationFormData,
            newOrExistingSite: "", // nothing selected
            isThisNewOrExistingSite: "", // mirrors the API field; must not keep a stale "existing"
          };
          delete next.existingSiteId;
          // Keep the row's identity (id) so the same facility row is reused
          const keepId = (existingForm as { id?: unknown }).id;
          if (keepId != null && String(keepId).trim() !== "") next.id = keepId;
          updatedForms[maufactureFacilityInfoKey] = next;
          return updatedForms;
        });
        return;
      }

      if (isCleared) {
        setManufacturingFacilityInformationForms(prevForms => {
          const updatedForms = [...prevForms];
          const existingForm: IManufacturingFacilityInformationFormData =
            updatedForms?.[maufactureFacilityInfoKey] ?? {
              name: "",
              contact: "",
              emailId: "",
              phone: "",
              manufacturerZip: "",
              manufacturerAddress: "",
              manufacturerCity: "",
              manufacturerState: "",
              manufacturerCountry: "",
            };
          updatedForms[maufactureFacilityInfoKey] = {
            ...existingForm,
            [field]: "" as any,
            ...(field === "manufacturerCountry"
              ? {
                  manufacturerAddress: "",
                  manufacturerCity: "",
                  manufacturerState: "",
                  manufacturerZip: "",
                }
              : {}),
          };
          return updatedForms;
        });
        if (field === "manufacturerCountry") {
          setAddressSuggestions([]);
        }
        return;
      }

      const fieldValue = option.value; // Keep original type (number)
        
    // update the vendorInformationFormData state
    setManufacturingFacilityInformationForms(prevForms => {
      const updatedForms = [...prevForms];
      const existingForm: IManufacturingFacilityInformationFormData =
        updatedForms?.[maufactureFacilityInfoKey] ?? {
          name: "",
          contact: "",
          emailId: "",
          phone: "",
          manufacturerZip: "",
          manufacturerAddress: "",
          manufacturerCity: "",
          manufacturerState: "",
          manufacturerCountry: "",
        };
      const next = { ...existingForm, [field]: fieldValue };
      if (field === "newOrExistingSite") {
        if (fieldValue === "new") {
          if (!hasExistingVendor && !useSharedExistingSitesCatalog) {
            setExistingSitesList([]);
          }
          delete (next as any).existingSiteId;
          // Clear all site fields so user can enter new site details (useEffect will clear selectedExistingSiteId when not "existing")
          next.name = "";
          next.contact = "";
          next.emailId = "";
          next.phone = "";
          next.manufacturerAddress = "";
          next.manufacturerCity = "";
          next.manufacturerState = "";
          next.manufacturerZip = "";
          next.manufacturerCountry = "";
        } else if (fieldValue === "existing") {
          delete (next as any).existingSiteId;
          // Clear all site fields until user selects a site from the dropdown
          next.name = "";
          next.contact = "";
          next.emailId = "";
          next.phone = "";
          next.manufacturerAddress = "";
          next.manufacturerCity = "";
          next.manufacturerState = "";
          next.manufacturerZip = "";
          next.manufacturerCountry = "";
        }
      }
      updatedForms[maufactureFacilityInfoKey] = next;
      return updatedForms;  
    });
    // Fetch existing sites when TJ user changes to "Existing" (guest: useEffect when assessment id is ready)
    if (
      field === "newOrExistingSite" &&
      fieldValue === "existing" &&
      isVendor !== true
    ) {
      fetchExistingSites();
    }
    
      if (field === 'manufacturerCountry') {
        setAddressSuggestions([]);
        // Clear address fields when country changes
        setManufacturingFacilityInformationForms(prevForms => {
          const updatedForms = [...prevForms];
          const existingForm: IManufacturingFacilityInformationFormData =
            updatedForms?.[maufactureFacilityInfoKey] ?? {
              name: "",
              contact: "",
              emailId: "",
              phone: "",
              manufacturerZip: "",
              manufacturerAddress: "",
              manufacturerCity: "",
              manufacturerState: "",
              manufacturerCountry: "",
            };
          updatedForms[maufactureFacilityInfoKey] = {
            ...existingForm,
            manufacturerAddress: '',
            manufacturerCity: '',
            manufacturerState: '',
            manufacturerZip: '',
          };
          return updatedForms;
        });
      }
    };
  

  const handleFieldFocus = (field: keyof IManufacturingFacilityInformationFormData) => {
    const updatedErrors = Array.isArray(manufacturingFacilityInformationFormErrors) 
      ? [...manufacturingFacilityInformationFormErrors] 
      : [];

    updatedErrors[maufactureFacilityInfoKey] = {
      ...updatedErrors[maufactureFacilityInfoKey],  // Spread the previous errors for this tab
      [field]: false,
    };

    setManufacturingFacilityInformationFormErrors(updatedErrors);
  };

  const handleBlur = async (
    field: keyof IManufacturingFacilityInformationFormData
  ) => {
    const updatedErrors = Array.isArray(manufacturingFacilityInformationFormErrors) 
      ? [...manufacturingFacilityInformationFormErrors] 
      : [];

    // Remove error if field has a value (handles browser autofill)
    if (manufacturingFacilityInformationFormData[field]) {
      updatedErrors[maufactureFacilityInfoKey] = {
        ...updatedErrors[maufactureFacilityInfoKey],
        [field]: false,
      };
    }

    // Checking if the field is empty.
    if (
      isVendorAssessmentSubmitClicked &&
      !manufacturingFacilityInformationFormData[field] &&
      manufacturingFormPermissions[field].isMandatory
    ) {
      updatedErrors[maufactureFacilityInfoKey] = {
        ...updatedErrors[maufactureFacilityInfoKey], 
        [field]: true,
      };
    }
    if (
      [
        "manufacturerCity",
        "manufacturerState",
        // "manufacturerCountry",
        "manufacturerZip",
      ].includes(field)
    ) {
      const address = {
        street:
          manufacturingFacilityInformationFormData.manufacturerAddress ?? "",
        city: manufacturingFacilityInformationFormData.manufacturerCity ?? "",
        state:
          manufacturingFacilityInformationFormData.manufacturerState ?? "",
        // country:
        //   manufacturingFacilityInformationFormData.manufacturerCountry ?? "",
        postalCode:
          manufacturingFacilityInformationFormData.manufacturerZip ?? "",
      };
      // Compare current address with the previous address
      const hasAddressChanged = Object.keys(address).some(
        (key) =>
          address[key as keyof typeof address] !==
          previousAddressRef.current[key as keyof typeof address]
      );
      if (hasAddressChanged) {
        // setIsLoading(true);
        // try {
        //   const isValid = await validateAddress(
        //     address,
        //     messageTexts,
        //     setIsLoading
        //   );
        //   if (isValid) {
        //     setIsModalOpen(true);
        //   } else {
        //     showToast({
        //       type: "error",
        //       message:
        //         toastMessageTexts?.MELISSA_ADDRESS_VALIDATION
        //           ?.GLOBAL_ADDRESS_VALIDATION_API_ERROR,
        //     });
        //     setIsModalOpen(true);
        //   }
        // } catch (error) {
        //   console.error("Error validating address:", error);
        // } finally {
        //   setIsLoading(false);
        // }
        setAddressConfirmationMessage(messageTexts);
        setIsModalOpen(true);
        // Update the previous address ref
        previousAddressRef.current = address;
      }
      if (field === "manufacturerZip") {
        const manufacturerZipValue = (manufacturingFacilityInformationFormData[field] ?? "") as string;
        if (manufacturerZipValue && manufacturerZipValue.trim().length < Constants.ZIP_CODE_MIN_LENGTH) {
          updatedErrors[maufactureFacilityInfoKey] = {
            ...updatedErrors[maufactureFacilityInfoKey], 
            [field]: true,
          };
          showToast({
            type: "error",
            message: validationMessages?.ZIP_CODE_MIN_LENGTH_ERROR
          });
        }
      }
    } else if (field === "emailId") {
      const emailValue = (manufacturingFacilityInformationFormData[field] ?? "") as string;
      if (emailValue.trim() !== "" && !isValidEmail(emailValue)) {
        updatedErrors[maufactureFacilityInfoKey] = {
        ...updatedErrors[maufactureFacilityInfoKey], 
        [field]: true,
        };
        showToast({
          type: "error",
          message: validationMessages?.EMAIL_VALIDATE_ERROR,
        });
      }
    }
    // Set the updated errors
    setManufacturingFacilityInformationFormErrors(updatedErrors);
  };

  const isFieldDisabled = (_fieldName: string) => isFormDisabled;

  /**
   * Persisted rows: delete only if ≥2 saved sites.
   * Unpersisted rows: delete if ≥2 unsaved sections, or if any persisted site exists (single new row is removable when saved sites remain).
   */
  const showHeaderDeleteButton = useMemo(() => {
    if (vendorKey !== undefined) return false;
    const row = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey];
    const rowRecord = (row ?? {}) as Record<string, unknown>;
    const hasPersistedOnRow = hasPersistedManufacturingFacilityId(rowRecord);
    const persistedCount =
      manufacturingFacilityInformationForms?.filter((f) =>
        hasPersistedManufacturingFacilityId((f ?? {}) as Record<string, unknown>)
      ).length ?? 0;
    const unpersistedCount =
      manufacturingFacilityInformationForms?.filter(
        (f) => !hasPersistedManufacturingFacilityId((f ?? {}) as Record<string, unknown>)
      ).length ?? 0;
    if (hasPersistedOnRow) {
      return persistedCount > 1;
    }
    return unpersistedCount > 1 || persistedCount > 0;
  }, [vendorKey, manufacturingFacilityInformationForms, maufactureFacilityInfoKey]);

  const closeDeletePersistedSiteModal = useCallback(() => {
    setIsDeletePersistedSiteModalOpen(false);
  }, []);

  const handleConfirmPersistedSiteDelete = useCallback(async () => {
    try {
      if (onPersistedDeleteConfirm) {
        await onPersistedDeleteConfirm();
      } else {
        onDelete();
      }
    } finally {
      closeDeletePersistedSiteModal();
    }
  }, [closeDeletePersistedSiteModal, onDelete, onPersistedDeleteConfirm]);

  const handleHeaderDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const row = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey];
      if (hasPersistedManufacturingFacilityId((row ?? {}) as Record<string, unknown>)) {
        setConfirmationTittle(deleteSiteConfirmTitle ?? "Delete this site");
        setConfirmationMessage(
          deleteSiteConfirmMessage ??
            "Are you sure you want to delete this site? This action cannot be undone."
        );
        setHighlightedConfirmationMessage("");
        setIsDeletePersistedSiteModalOpen(true);
      } else {
        onDelete();
      }
    },
    [
      manufacturingFacilityInformationForms,
      maufactureFacilityInfoKey,
      onDelete,
      deleteSiteConfirmTitle,
      deleteSiteConfirmMessage,
    ]
  );

  return (
    <Box
      className={`form-container ${isExpanded ? "form-container-expanded" : ""
        }`}
    >
      <Box
        className={`form-header cursor-pointer ${isExpanded ? "form-header-expanded" : ""}`}
        onClick={onToggle}
      >
        <Box className="inline-flex gap-1 align-middle">
          <FormHeader
            title={
              manufacturingFacilityInformationForms?.length > 1
                ? `${formHeader} #${maufactureFacilityInfoKey + 1}`
                : formHeader
            }
            isExpanded={isExpanded}
          />
          {showHeaderDeleteButton && (
            <IconButton
              size="small"
              onClick={handleHeaderDeleteClick}
              sx={{
                width: "31px",
                height: "30px",
                color: "#C81E24",
                padding: "8px",
                ...(isExpanded ? { "& img": { filter: "brightness(0) invert(1)" } } : {}),
              }}
            >
              <Image src={DeleteIcon} alt="Delete" />
            </IconButton>
          )}
        </Box>

        <Box className="pr-4 cursor-pointer">
          {isExpanded ? UpArrow("dark") : DownArrow(displayMode())}
        </Box>
      </Box>
      <form onSubmit={(e) => e.preventDefault()}>
        {isExpanded && (
          <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1.5rem] p-[1.5rem]">
            {/* Is This New or Existing Site? - only when vendor is existing vendor (hasExistingVendor from API) */}
            {hasExistingVendor && (
              <Box>
                <Label
                  className={darkModeClass}
                  text={manufacturingFacilityLabels?.IS_THIS_NEW_OR_EXISTING_SITE ?? "Is This New or Existing Site?"}
                  htmlFor={`newOrExistingSite-${maufactureFacilityInfoKey}`}
                />
                <DropdownAutoSuggest
                  options={NEW_OR_EXISTING_SITE_OPTIONS}
                  value={(() => {
                    const raw = (manufacturingFacilityInformationFormData as any)?.newOrExistingSite;
                    if (raw == null || raw === "") return null;
                    const option = NEW_OR_EXISTING_SITE_OPTIONS.find((o) => String(o.value) === String(raw));
                    return option ?? null;
                  })()}
                  placeholder={languageText?.PLACEHOLDERS?.COMMON?.DROP_DOWN}
                  onChange={(option: any) => {
                      handleDropdownChange("newOrExistingSite")({} as React.ChangeEvent<HTMLInputElement>, option);
                  }}
                  onFocus={() => handleFieldFocus("newOrExistingSite")}
                  onBlur={() => handleBlur("newOrExistingSite")}
                  isDisabled={newOrExistingSiteDropdownDisabled}
                  className={darkModeClass}
                />
              </Box>
            )}
            {/* Name of Manufacturing Facility - dropdown when "Existing" (from API), input when "New" */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.name)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.NAME_OF_MANUFACTURING_FACILITY}
                htmlFor={`name-${maufactureFacilityInfoKey}`}
                required={manufacturingFormPermissions?.name?.isMandatory}
              />
              {hasExistingVendor && isExistingSite ? (
                effectiveIsLoadingExistingSites ? (
                  <Input
                    id={`name-${maufactureFacilityInfoKey}`}
                    name="name"
                    className={darkModeClass}
                    value=""
                    disabled
                    placeholder={languageText?.PLACEHOLDERS?.COMMON?.LOADING ?? "Loading..."}
                  />
                ) : (
                  <DropdownAutoSuggest
                    options={existingSiteDropdownOptionsForRow}
                    value={manufacturingFacilitySelectedExistingOption}
                    placeholder={
                      effectiveExistingSitesList.length > 0
                        ? languageText?.PLACEHOLDERS?.COMMON?.DROP_DOWN || "Please select an option"
                        : languageText?.PLACEHOLDERS?.COMMON?.NO_OPTIONS ?? "No existing sites"
                    }
                    onChange={(option: any) => {
                        handleExistingSiteSelect(option);
                    }}
                    onFocus={() => handleFieldFocus("name")}
                    onBlur={() => handleBlur("name")}
                    isDisabled={!manufacturingFormPermissions?.name?.isEditable || isFieldDisabled("name")}
                    className={`${darkModeClass} ${(currentFormErrors?.name) ? "field-error-color" : ""}`}
                  />
                )
              ) : (
                <Input
                  id={`name-${maufactureFacilityInfoKey}`}
                  name="name"
                  className={`${darkModeClass} ${(currentFormErrors?.name)
                      ? "field-error-color"
                      : ""
                    }`}
                  value={manufacturingFacilityInformationFormData?.name}
                  onChange={handleInputChange("name")}
                  onInput={handleInput("name")}
                  disabled={!manufacturingFormPermissions?.name?.isEditable || isFieldDisabled("name")}
                  onFocus={() => handleFieldFocus("name")}
                  onBlur={() => handleBlur("name")}
                  maxLength={Constants?.VA_INPUT_MAX_LENGTH}
                />
              )}
            </Box>

            {/* Manufacturing Contact  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.contact)
                  ? "label-error-text"
                  : ""
                }`}
                text={manufacturingFacilityLabels?.MANUFACTURING_CONTACT}
                htmlFor={`contact-${maufactureFacilityInfoKey}`}
                required={manufacturingFormPermissions?.contact?.isMandatory}
              />
              <Input
                id={`contact-${maufactureFacilityInfoKey}`}
                name="contact"
                className={`${darkModeClass} ${(currentFormErrors?.contact)
                    ? "field-error-color"
                    : ""
                  }`}
                value={manufacturingFacilityInformationFormData?.contact}
                onChange={handleInputChange("contact")}
                onInput={handleInput("contact")}
                disabled={!manufacturingFormPermissions?.contact?.isEditable || isFieldDisabled("contact")}
                onFocus={() => handleFieldFocus("contact")}
                onBlur={() => handleBlur("contact")}
                maxLength={Constants?.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Manufacturer’s Email ID  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.emailId)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_EMAIL_ID}
                htmlFor="emailId"
                required={manufacturingFormPermissions?.emailId?.isMandatory}
              />
              <Input
               className={`${darkModeClass} ${(currentFormErrors?.emailId)
                    ? "field-error-color"
                    : ""
                  }`}
                value={manufacturingFacilityInformationFormData?.emailId}
                onChange={handleInputChange("emailId")}
                onInput={handleInput("emailId")}
                disabled={!manufacturingFormPermissions?.emailId?.isEditable || isFieldDisabled("emailId")}
                onFocus={() => handleFieldFocus("emailId")}
                onBlur={() => handleBlur("emailId")}
                maxLength={Constants?.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Manufacturer’s Phone  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.phone)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_PHONE}
                htmlFor={`phone-${maufactureFacilityInfoKey}`}
                required={manufacturingFormPermissions?.phone?.isMandatory}
              />
              <Input
                id={`phone-${maufactureFacilityInfoKey}`}
                name="phone"
                className={`${darkModeClass} ${(currentFormErrors?.phone)
                    ? "field-error-color"
                    : ""
                  }`}
                value={manufacturingFacilityInformationFormData?.phone}
                onChange={handleInputChange("phone")}
                onInput={handleInput("phone")}
                disabled={!manufacturingFormPermissions?.phone?.isEditable || isFieldDisabled("phone")}
                onFocus={() => handleFieldFocus("phone")}
                onBlur={() => handleBlur("phone")}
                maxLength={Constants?.VA_INPUT_MAX_LENGTH}
              />
            </Box>

            {/* Manufacturer’s Country  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerCountry)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_COUNTRY}
                htmlFor="manufacturerCountry"
                required={
                  manufacturingFormPermissions?.manufacturerCountry?.isMandatory
                }
              />
               {dropdownData.countries.length > 0 ? (
                <DropdownAutoSuggest
                  className={`${darkMode ? "dark" : "light"} ${
                    currentFormErrors?.manufacturerCountry ? "field-error-color" : ""
                  } `}
                  options={formatRawDropdownOptions(
                    dropdownData.countries,
                    "name",
                    "id"
                  )}
                  value={(() => {
                    const countryId = manufacturingFacilityInformationForms?.[maufactureFacilityInfoKey]?.manufacturerCountry;
                    if (!countryId) return null;
                    const country = dropdownData.countries.find(
                      (c: any) => toNumberIfPossible(c.id) === toNumberIfPossible(countryId)
                    );
                    return country ? { label: country.name, value: toNumberIfPossible(country.id) } : null;
                  })()}
                  placeholder={languageText?.PLACEHOLDERS?.COMMON?.DROP_DOWN}
                  onChange={(option) => {
                    handleDropdownChange("manufacturerCountry")(
                      {} as React.ChangeEvent<HTMLInputElement>,
                      option
                    );
                  }}
                  onFocus={() => handleFieldFocus("manufacturerCountry")}
                  onBlur={() => handleBlur("manufacturerCountry")}
                  isDisabled={isFieldDisabled("manufacturerCountry")}
                  isClearable
                />
              ) : (
                <div className="text-gray-500 text-sm">Loading countries...</div>
              )}
              {/* <Input
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerCountry)
                    ? "field-error-color"
                    : ""
                  }`}
                value={
                  manufacturingFacilityInformationFormData?.manufacturerCountry
                }
                onChange={handleInputChange("manufacturerCountry")}
                disabled={
                  !manufacturingFormPermissions?.manufacturerCountry
                    ?.isEditable || isFieldDisabled("manufacturerCountry")
                }
                onFocus={() => handleFieldFocus("manufacturerCountry")}
                onBlur={() => handleBlur("manufacturerCountry")}
                maxLength={Constants?.VA_INPUT_MAX_LENGTH}
              /> */}
            </Box>
            {/* Manufacturer’s Address  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerAddress)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_ADDRESS}
                htmlFor="manufacturer-address-input"
                required={
                  manufacturingFormPermissions?.manufacturerAddress?.isMandatory
                }
              />

              <AutoComplete
                suggestions={addressSuggestions}
                inputValue={
                  manufacturingFacilityInformationFormData?.manufacturerAddress ?? ""
                }
                setInputValue={handleAutoCompleteInputChange(
                  "manufacturerAddress"
                )}
                onSuggestionsFetchRequested={() =>
                  handleSuggestionsFetchRequested(
                    payloadRef,
                    setAddressSuggestions
                  )
                }
                onSuggestionsClearRequested={onSuggestionsClearRequested}
                getSuggestionValue={(suggestion: any) =>
                  suggestion?.Address?.Address1
                }
                renderSuggestion={(suggestion: any) => (
                  <span>{suggestion?.Address?.Address}</span>
                )}
                onSuggestionSelected={onSuggestionSelected}
                disabled={
                  !manufacturingFormPermissions?.manufacturerAddress
                    ?.isEditable || isFieldDisabled("manufacturerAddress")
                }
                onFocus={() => handleFieldFocus("manufacturerAddress")}
                onBlur={() => handleBlur("manufacturerAddress")}
                inputProps={{
                  id: "manufacturer-address-input",
                  name: "manufacturerAddress",
                  className: `${darkMode ? "dark" : "light"} ${currentFormErrors?.manufacturerAddress ? "field-error-color" : ""} `
                }}
                maxLength={Constants.SITE_ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Manufacturer’s City  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerCity)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_CITY}
                htmlFor={`manufacturerCity-${maufactureFacilityInfoKey}`}
                required={
                  manufacturingFormPermissions?.manufacturerCity?.isMandatory
                }
              />
              <Input
                id={`manufacturerCity-${maufactureFacilityInfoKey}`}
                name="manufacturerCity"
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerCity)
                    ? "field-error-color"
                    : ""
                  }`}
                value={
                  manufacturingFacilityInformationFormData?.manufacturerCity
                }
                onChange={handleInputChange("manufacturerCity")}
                onInput={handleInput("manufacturerCity")}
                disabled={
                  !manufacturingFormPermissions?.manufacturerCity?.isEditable || isFieldDisabled("manufacturerCity")
                }
                onFocus={() => handleFieldFocus("manufacturerCity")}
                onBlur={() => handleBlur("manufacturerCity")}
                maxLength={Constants.ADDRESS_MAX_LENGTH}
              />
            </Box>

            {/* Manufacturer’s State  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerState)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_STATE}
                htmlFor={`manufacturerState-${maufactureFacilityInfoKey}`}
                required={
                  manufacturingFormPermissions?.manufacturerState?.isMandatory
                }
              />
              <Input
                id={`manufacturerState-${maufactureFacilityInfoKey}`}
                name="manufacturerState"
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerState)
                    ? "field-error-color"
                    : ""
                  }`}
                value={
                  manufacturingFacilityInformationFormData?.manufacturerState
                }
                onChange={handleInputChange("manufacturerState")}
                onInput={handleInput("manufacturerState")}
                disabled={
                  !manufacturingFormPermissions?.manufacturerState?.isEditable || isFieldDisabled("manufacturerState")
                }
                onFocus={() => handleFieldFocus("manufacturerState")}
                onBlur={() => handleBlur("manufacturerState")}
                maxLength={Constants?.ADDRESS_STATE_MAX_LENGTH}
              />
            </Box>
            {/* Manufacturer’s Zip  */}
            <Box>
              <Label
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerZip)
                   ? "label-error-text"
                    : ""
                  }`}
                text={manufacturingFacilityLabels?.MANUFACTURERS_ZIP}
                htmlFor={`manufacturerZip-${maufactureFacilityInfoKey}`}
                required={
                  manufacturingFormPermissions?.manufacturerZip?.isMandatory
                }
              />
              <Input
                id={`manufacturerZip-${maufactureFacilityInfoKey}`}
                name="manufacturerZip"
                className={`${darkModeClass} ${(currentFormErrors?.manufacturerZip)
                    ? "field-error-color"
                    : ""
                  }`}
                value={
                  manufacturingFacilityInformationFormData?.manufacturerZip
                }
                onChange={handleInputChange("manufacturerZip")}
                onInput={handleInput("manufacturerZip")}
                disabled={
                  !manufacturingFormPermissions?.manufacturerZip?.isEditable || isFieldDisabled("manufacturerZip")
                }
                onFocus={() => handleFieldFocus("manufacturerZip")}
                onBlur={() => handleBlur("manufacturerZip")}
                minLength={Constants?.ZIP_CODE_MIN_LENGTH}
                maxLength={Constants?.ZIP_CODE_MAX_LENGTH}
              />
            </Box>

            

            <ConfirmationModal
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => setIsModalOpen(false)}
              onDecline={handleSelectAlternative}
              confirmText="Confirm Entered"
              cancelText="Select Alternative"
              showCancelButton={true}
            />
          </Box>
        )}
      </form>
      <ConfirmationModal
        open={isDeletePersistedSiteModalOpen}
        onClose={closeDeletePersistedSiteModal}
        onDecline={closeDeletePersistedSiteModal}
        onConfirm={handleConfirmPersistedSiteDelete}
        confirmText={buttonTexts?.DELETE ?? "Delete"}
        cancelText={buttonTexts?.CANCEL_BUTTON ?? "Cancel"}
        showCancelButton={true}
      />
    </Box>
  );
};

export default ManufacturingFacilityInformationForm;